import React, { createContext, useContext } from "react";
import { Alert, ConfigProvider } from "antd";
import type { StepKey } from "./wizardSteps";
import type { WizardOp, WizardStepAccess } from "./wizardPermissions";
import useWizardStepAccess from "./useWizardStepAccess";

const ALL_ALLOWED: WizardStepAccess = { view: true, create: true, update: true, delete: true };

// A step rendered outside a WizardStepGate (a unit test mounting it alone)
// behaves as it did before the wizard had permissions.
const WizardStepAccessContext = createContext<WizardStepAccess>(ALL_ALLOWED);

/** What the user may do on the step being rendered. */
export function useCurrentWizardStepAccess(): WizardStepAccess {
  return useContext(WizardStepAccessContext);
}

/**
 * Renders one wizard page under its Wizard / Installer permissions: a notice
 * instead of the page without `view`, and the page with every control
 * disabled when the user may view it but change nothing. Finer gates on the
 * page's own add, edit and delete controls use WizardOpGate.
 */
export function WizardStepGate({ step, children }: { step: StepKey; children: React.ReactNode }) {
  const access = useWizardStepAccess(step);

  if (!access.view) {
    return (
      <Alert
        type="info"
        showIcon
        data-testid="wizard-step-no-permission"
        message="You don't have permission to view this page"
        description="Ask an administrator for this page's Wizard / Installer view permission."
      />
    );
  }

  const readOnly = !access.create && !access.update && !access.delete;

  return (
    <WizardStepAccessContext.Provider value={access}>
      {readOnly ? <ConfigProvider componentDisabled>{children}</ConfigProvider> : children}
    </WizardStepAccessContext.Provider>
  );
}

/**
 * Disables the controls inside it unless the user holds `op` on the current
 * wizard page. Wrap a page's add buttons in op="create", its save and edit
 * controls in op="update", and its delete controls in op="delete".
 */
export function WizardOpGate({ op, children }: { op: WizardOp; children: React.ReactNode }) {
  const access = useCurrentWizardStepAccess();

  if (access[op]) return <>{children}</>;
  return <ConfigProvider componentDisabled>{children}</ConfigProvider>;
}
